// ============ Benchmark Engine: Dynamic Agent Execution ============
// This engine replaces hardcoded simulation steps with dynamic,
// task-aware agent execution that generates real tool calls and traces.

import type { Task, Trace, TraceStep, AgentConfig } from './types';
import { executeTool } from './ToolExecutor';

// ---- Task analysis ----
interface TaskAnalysis {
  domain: 'tourism' | 'business' | 'finance' | 'generic';
  keywords: string[];
  budget?: number;
  destination?: string;
  duration_days?: number;
  travelers?: { adults: number; children: number };
  has_constraints: boolean;
  requires_comparison: boolean;
  requires_risk_assessment: boolean;
}

function analyzeTask(task: Task): TaskAnalysis {
  const text = `${task.title} ${task.description}`.toLowerCase();

  const domain: TaskAnalysis['domain'] =
    text.includes('旅游') || text.includes('travel') || text.includes('trip') || text.includes('游')
      ? 'tourism'
      : text.includes('商业') || text.includes('business') || text.includes('市场') || text.includes('战略')
        ? 'business'
        : text.includes('财务') || text.includes('finance') || text.includes('投资') || text.includes('预算分配')
          ? 'finance'
          : 'generic';

  // Extract budget
  const budgetMatch = text.match(/(?:预算|budget)[:：\s]*[¥￥]?\s*([\d,]+)/);
  const budget = budgetMatch ? parseInt(budgetMatch[1].replace(/,/g, '')) : undefined;

  // Extract destination
  const destMatch = text.match(/(?:杭州|上海|北京|成都|西安|南京|苏州|厦门|青岛|大连)/);
  const destination = destMatch ? destMatch[0] : undefined;

  // Extract duration
  const durMatch = text.match(/(\d+)\s*(?:日|天|day)/);
  const duration_days = durMatch ? parseInt(durMatch[1]) : undefined;

  // Extract travelers
  const adultMatch = text.match(/(\d+)\s*(?:大|成人|adult)/);
  const childMatch = text.match(/(\d+)\s*(?:小|儿童|child|kid)/);
  const travelers = (adultMatch || childMatch)
    ? { adults: adultMatch ? parseInt(adultMatch[1]) : 0, children: childMatch ? parseInt(childMatch[1]) : 0 }
    : undefined;

  const has_constraints = text.includes('预算') || text.includes('必须') || text.includes('要求') || text.includes('budget');
  const requires_comparison = text.includes('比价') || text.includes('对比') || text.includes('compare') || text.includes('多方案');
  const requires_risk_assessment = text.includes('天气') || text.includes('风险') || text.includes('备选') || text.includes('weather') || text.includes('risk');

  const keywords = text.split(/[\s,，。、\n]+/).filter(w => w.length > 1).slice(0, 10);

  return { domain, keywords, budget, destination, duration_days, travelers, has_constraints, requires_comparison, requires_risk_assessment };
}

// ---- Step plan generation ----
interface PlannedStep {
  type: 'thinking' | 'tool_call';
  tool?: string;
  args?: any;
  reasoning: string;
}

function generatePlan(analysis: TaskAnalysis, task: Task): PlannedStep[] {
  const steps: PlannedStep[] = [];
  const tools = task.tools_allowed;

  const hasTool = (name: string) =>
    tools.some(t => t.toLowerCase().includes(name.toLowerCase()));

  // Step 1: Analyze task
  steps.push({
    type: 'thinking',
    reasoning: `分析任务需求：${task.title}。${analysis.budget ? `预算¥${analysis.budget}，` : ''}${analysis.destination ? `目的地${analysis.destination}，` : ''}${analysis.duration_days ? `${analysis.duration_days}天行程。` : ''}需要搜集交通、住宿、景点等信息。`,
  });

  // Step 2: Search for transport (if tourism)
  if (analysis.domain === 'tourism') {
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: `${analysis.destination || '杭州'} 高铁 交通 时刻表 价格` },
        reasoning: '搜索交通信息，了解出发地到目的地的交通方式和费用',
      });
    }

    // Step 3: Search for hotels
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: `${analysis.destination || '杭州'} 酒店 住宿 ${analysis.budget ? `预算${analysis.budget}` : ''}` },
        reasoning: '搜索住宿选项，根据预算筛选合适的酒店',
      });
    }

    // Step 4: Search for attractions
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: `${analysis.destination || '杭州'} 景点 必去 ${analysis.travelers?.children ? '亲子' : ''}` },
        reasoning: '搜索景点信息，了解门票价格和游览时长',
      });
    }

    // Step 5: Check weather (if risk assessment needed)
    if (analysis.requires_risk_assessment && (hasTool('search') || hasTool('browser'))) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: `${analysis.destination || '杭州'} 天气预报` },
        reasoning: '查询天气预报，评估是否需要调整行程或准备备选方案',
      });
    }

    // Step 6: Map/route planning
    if (hasTool('maps')) {
      steps.push({
        type: 'tool_call',
        tool: 'Maps',
        args: { origin: '上海', destination: analysis.destination || '杭州', mode: 'transit' },
        reasoning: '查询路线信息，计算交通距离和时长',
      });
    }

    // Step 7: Search for restaurants
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: `${analysis.destination || '杭州'} 美食 餐厅 推荐` },
        reasoning: '搜索餐饮信息，了解当地特色美食和餐厅价格',
      });
    }
  }

  // Business domain steps
  if (analysis.domain === 'business') {
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: '行业市场分析 竞争格局 增长趋势' },
        reasoning: '搜索行业市场数据，了解市场规模和增长趋势',
      });
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: '竞争对手分析 SWOT 市场份额' },
        reasoning: '搜索竞争对手信息，进行SWOT分析',
      });
    }
  }

  // Finance domain steps
  if (analysis.domain === 'finance') {
    if (hasTool('search') || hasTool('browser')) {
      steps.push({
        type: 'tool_call',
        tool: 'Search',
        args: { query: '投资组合配置 资产分配 风险评估' },
        reasoning: '搜索投资信息，了解不同资产的收益率和风险',
      });
    }
  }

  // Step 8: Budget calculation
  if (analysis.budget && hasTool('calculator')) {
    steps.push({
      type: 'tool_call',
      tool: 'Calculator',
      args: { expression: 'hotel + transport + food + attractions' },
      reasoning: `计算总费用，确保在预算¥${analysis.budget}以内`,
    });
  }

  // Step 9: Comparison (if needed)
  if (analysis.requires_comparison && hasTool('spreadsheet')) {
    steps.push({
      type: 'tool_call',
      tool: 'Spreadsheet',
      args: { action: 'compare', data: [] },
      reasoning: '使用电子表格对比多个方案，选择最优组合',
    });
  }

  // Step 10: Analysis (if spreadsheet available)
  if (hasTool('spreadsheet') && !analysis.requires_comparison) {
    steps.push({
      type: 'tool_call',
      tool: 'Spreadsheet',
      args: { action: 'analyze', data: [] },
      reasoning: '整理和分析搜集到的数据，形成结构化方案',
    });
  }

  // Step 11: Final reasoning
  steps.push({
    type: 'thinking',
    reasoning: `综合所有信息，制定最终方案。${analysis.has_constraints ? '需确保满足所有约束条件。' : ''}${analysis.requires_risk_assessment ? '需包含风险应对方案。' : ''}`,
  });

  // Step 12: Finish
  steps.push({
    type: 'thinking',
    reasoning: '方案制定完成，准备输出最终结果',
  });

  return steps;
}

// ---- Generate final answer ----
function generateFinalAnswer(analysis: TaskAnalysis, steps: TraceStep[], task: Task): any {
  const toolCalls = steps.filter(s => s.action.type === 'tool_call');
  const successfulCalls = toolCalls.filter(s => s.observation?.status === 'success');

  if (analysis.domain === 'tourism') {
    const hotels = toolCalls
      .find(s => s.action.args?.query?.includes('酒店'))
      ?.observation?.data?.hotels ?? [];
    const trains = toolCalls
      .find(s => s.action.args?.query?.includes('高铁') || s.action.args?.query?.includes('交通'))
      ?.observation?.data?.trains ?? [];
    const attractions = toolCalls
      .find(s => s.action.args?.query?.includes('景点'))
      ?.observation?.data?.attractions ?? [];
    const weather = toolCalls
      .find(s => s.action.args?.query?.includes('天气'))
      ?.observation?.data?.forecast;
    const calcResult = toolCalls
      .find(s => s.action.tool === 'Calculator')
      ?.observation?.data;

    const selectedHotel = hotels[0] ?? HOTEL_FALLBACK;
    const selectedTrain = trains[0] ?? TRAIN_FALLBACK;

    return {
      type: 'travel_itinerary',
      summary: `${task.title} - 完整行程方案`,
      transport: {
        type: '高铁',
        train: selectedTrain.trainNo ?? 'G7341',
        depart: selectedTrain.depart ?? '07:00',
        arrive: selectedTrain.arrive ?? '08:15',
        price: selectedTrain.price ?? 73,
      },
      accommodation: {
        hotel: selectedHotel.name ?? '杭州黄龙饭店',
        price_per_night: selectedHotel.price ?? 520,
        rating: selectedHotel.rating ?? 4.5,
        distance_to_center: selectedHotel.distance ?? 2.1,
      },
      attractions: (attractions ?? ATTRACTION_FALLBACK).slice(0, 4).map((a: any) => ({
        name: a.name,
        ticket: a.ticket,
        duration: a.duration,
      })),
      weather: weather ?? { condition: '晴', temp: 28 },
      budget: {
        total: calcResult?.result ?? (selectedHotel.price + selectedTrain.price * 2 + 200),
        limit: analysis.budget ?? '未限定',
        within_budget: analysis.budget
          ? (calcResult?.result ?? selectedHotel.price + selectedTrain.price * 2 + 200) <= analysis.budget
          : true,
      },
      tools_used: successfulCalls.length,
      steps_total: steps.length,
    };
  }

  if (analysis.domain === 'business') {
    return {
      type: 'business_analysis',
      summary: `${task.title} - 市场分析报告`,
      market_overview: '行业增长迅速，竞争格局集中',
      swot: {
        strengths: ['技术积累', '品牌优势'],
        weaknesses: ['市场进入较晚', '渠道不足'],
        opportunities: ['政策支持', '消费升级'],
        threats: ['竞争激烈', '监管变化'],
      },
      competitors: 3,
      roi_estimate: '15-25%',
      tools_used: successfulCalls.length,
      steps_total: steps.length,
    };
  }

  if (analysis.domain === 'finance') {
    return {
      type: 'financial_plan',
      summary: `${task.title} - 财务规划方案`,
      asset_allocation: { stocks: 40, bonds: 30, cash: 20, alternatives: 10 },
      expected_return: '6.5%',
      risk_level: '中等',
      tools_used: successfulCalls.length,
      steps_total: steps.length,
    };
  }

  return {
    type: 'generic',
    summary: `${task.title} - 分析完成`,
    tools_used: successfulCalls.length,
    steps_total: steps.length,
  };
}

// Fallback data
const HOTEL_FALLBACK = { name: '杭州黄龙饭店', price: 520, rating: 4.5, distance: 2.1 };
const TRAIN_FALLBACK = { trainNo: 'G7341', depart: '07:00', arrive: '08:15', price: 73 };
const ATTRACTION_FALLBACK = [
  { name: '西湖', ticket: 0, duration: '3-4h' },
  { name: '灵隐寺', ticket: 30, duration: '2h' },
  { name: '河坊街', ticket: 0, duration: '2h' },
  { name: '雷峰塔', ticket: 40, duration: '1h' },
];

// ---- Main execution function ----
export interface ExecutionCallbacks {
  onStep: (step: TraceStep) => void;
  onComplete: (trace: Trace) => void;
  onError: (error: string) => void;
}

export async function executeBenchmark(
  task: Task,
  agentConfig: AgentConfig,
  callbacks: ExecutionCallbacks,
): Promise<void> {
  const startTime = Date.now();
  const analysis = analyzeTask(task);
  const plan = generatePlan(analysis, task);

  const steps: TraceStep[] = [];
  const maxSteps = Math.min(agentConfig.max_steps || 50, plan.length + 5);

  for (let i = 0; i < Math.min(plan.length, maxSteps); i++) {
    const planned = plan[i];
    const stepNumber = i + 1;
    const timestamp = new Date().toISOString();

    // Build the step
    const step: TraceStep = {
      step_number: stepNumber,
      timestamp,
      action: {
        type: planned.type,
        ...(planned.tool && { tool: planned.tool }),
        ...(planned.args && { args: planned.args }),
        reasoning: planned.reasoning,
      },
    };

    // Execute tool calls
    if (planned.type === 'tool_call' && planned.tool) {
      const result = executeTool(planned.tool, planned.args ?? {});
      step.observation = {
        status: result.status,
        ...(result.data && { data: result.data }),
        ...(result.error && { error: result.error }),
      };
      step.state_diff = {
        tool: planned.tool,
        latency_ms: result.latency_ms,
        data_size: JSON.stringify(result.data ?? {}).length,
      };
    }

    steps.push(step);
    callbacks.onStep(step);

    // Realistic delay between steps (200-500ms)
    await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300));
  }

  // Generate final answer
  const finalAnswer = generateFinalAnswer(analysis, steps, task);

  // Add finish step
  const finishStep: TraceStep = {
    step_number: steps.length + 1,
    timestamp: new Date().toISOString(),
    action: {
      type: 'finish',
      args: { summary: finalAnswer.summary },
      reasoning: '任务完成，输出最终方案',
    },
  };
  steps.push(finishStep);
  callbacks.onStep(finishStep);

  const duration_ms = Date.now() - startTime;

  const trace: Trace = {
    trace_id: `trace-${Date.now()}`,
    task_id: task.id,
    agent_name: agentConfig.name,
    agent_type: 'model',
    steps,
    final_answer: finalAnswer,
    status: 'completed',
    steps_count: steps.length,
    duration_ms,
    created_at: new Date().toISOString(),
  };

  callbacks.onComplete(trace);
}

// ---- Multi-dimensional scoring ----
export interface MultiDimScore {
  accuracy: number;      // 40% - Did the agent produce a correct answer?
  process: number;       // 30% - Did the agent follow a logical decision process?
  efficiency: number;   // 20% - Did the agent use steps efficiently?
  constraint: number;    // 10% - Did the agent satisfy task constraints?
  total: number;
  breakdown: {
    accuracy: number;
    process: number;
    efficiency: number;
    constraint: number;
  };
  evidence: string[];
}

export function calculateMultiDimScore(trace: Trace, task: Task): MultiDimScore {
  const evidence: string[] = [];
  const steps = trace.steps ?? [];
  const toolCalls = steps.filter(s => s.action.type === 'tool_call');
  const successfulCalls = toolCalls.filter(s => s.observation?.status === 'success');
  const thinkingSteps = steps.filter(s => s.action.type === 'thinking');

  // Standardized final answer (parsed by parseFinalAnswer in AgentBridge)
  // Falls back gracefully for simulation mode (already structured)
  const fa: any = trace.final_answer ?? {};
  const parseMethod = fa._parse_method ?? (fa.summary ? 'json_block' : 'failed');
  const hasSummary = !!fa.summary || typeof fa === 'object' && fa.type;
  const budgetUsed = typeof fa.budget_used === 'number' ? fa.budget_used
    : (fa.budget?.total ?? fa.budget?.total_cost ?? (typeof fa.budget_used === 'number' ? fa.budget_used : undefined));
  const withinBudget = typeof fa.within_budget === 'boolean'
    ? fa.within_budget
    : (fa.budget?.within_budget ?? (budgetUsed !== undefined ? budgetUsed <= (extractTaskBudget(task) ?? Number.MAX_SAFE_INTEGER) : undefined));
  const items = Array.isArray(fa.items) ? fa.items
    : (fa.accommodation || fa.transport ? [fa.transport, fa.accommodation].filter(Boolean).map((x: any) => ({ name: x.name ?? x.hotel ?? x.train, price: x.price ?? x.price_per_night })) : []);
  const constraintsSatisfied: string[] = Array.isArray(fa.constraints_satisfied) ? fa.constraints_satisfied : [];

  // 1. Accuracy (40%) - Based on structured answer quality
  let accuracyScore = 0;
  if (hasSummary || trace.final_answer) {
    accuracyScore += 10;
    evidence.push('Agent生成了最终方案');
  }
  // Reward structured JSON output (parse success)
  if (parseMethod === 'json_block' || parseMethod === 'raw_json') {
    accuracyScore += 8;
    evidence.push('输出符合结构化JSON格式');
  } else if (parseMethod === 'regex_fallback') {
    accuracyScore += 3;
    evidence.push('输出为自由文本（regex解析）');
  }
  // Budget reported
  if (budgetUsed !== undefined) {
    accuracyScore += 7;
    evidence.push(`报告总花费¥${budgetUsed}`);
  }
  // Within budget
  if (withinBudget === true) {
    accuracyScore += 8;
    evidence.push('方案在预算范围内');
  } else if (withinBudget === false) {
    accuracyScore += 2;
    evidence.push('方案超出预算（仍给出数值）');
  }
  // Items with prices
  if (items.length >= 2) {
    accuracyScore += 7;
    evidence.push(`提供${items.length}项明细`);
  } else if (items.length === 1) {
    accuracyScore += 4;
  }
  accuracyScore = Math.min(40, accuracyScore);

  // 2. Process (30%) - Based on logical decision flow
  let processScore = 0;
  if (thinkingSteps.length >= 2) {
    processScore += 10;
    evidence.push(`包含${thinkingSteps.length}个推理步骤`);
  }
  if (toolCalls.length >= 4) {
    processScore += 10;
    evidence.push('多步骤工具调用，覆盖多个信息维度');
  }
  const toolTypes = new Set(toolCalls.map(s => s.action.tool));
  if (toolTypes.size >= 2) {
    processScore += 10;
    evidence.push(`使用了${toolTypes.size}种不同工具`);
  }
  processScore = Math.min(30, processScore);

  // 3. Efficiency (20%) - Based on step efficiency
  let efficiencyScore = 0;
  const expectedSteps = task.expected_steps || 30;
  const actualSteps = steps.length;
  const ratio = actualSteps / expectedSteps;
  if (ratio <= 1.0) {
    efficiencyScore = 20;
    evidence.push(`步骤数(${actualSteps})低于预期(${expectedSteps})，效率高`);
  } else if (ratio <= 1.5) {
    efficiencyScore = 15;
    evidence.push(`步骤数(${actualSteps})接近预期(${expectedSteps})`);
  } else if (ratio <= 2.0) {
    efficiencyScore = 10;
    evidence.push(`步骤数(${actualSteps})略高于预期(${expectedSteps})`);
  } else {
    efficiencyScore = 5;
    evidence.push(`步骤数(${actualSteps})远高于预期(${expectedSteps})，效率低`);
  }

  // 4. Constraint (10%) - Based on constraint satisfaction
  let constraintScore = 0;
  const taskText = `${task.title} ${task.description}`.toLowerCase();
  const hasBudgetConstraint = taskText.includes('预算') || taskText.includes('budget');
  if (hasBudgetConstraint) {
    if (withinBudget === true) {
      constraintScore += 5;
      evidence.push('满足预算约束');
    } else if (withinBudget === false) {
      constraintScore += 1;
      evidence.push('未满足预算约束');
    } else {
      constraintScore += 2; // Unknown
    }
  }
  // Must-visit constraint (e.g., 西湖)
  if (taskText.includes('西湖') || taskText.includes('必须')) {
    const answerText = JSON.stringify(trace.final_answer ?? {}).toLowerCase();
    const visitedWestLake = constraintsSatisfied.some(c => c.toLowerCase().includes('west_lake'))
      || answerText.includes('西湖') || answerText.includes('west lake');
    if (visitedWestLake) {
      constraintScore += 5;
      evidence.push('满足必去景点约束');
    }
  }
  if (constraintScore === 0) {
    constraintScore = 5; // No specific constraints to check
  }
  constraintScore = Math.min(10, constraintScore);

  const total = accuracyScore + processScore + efficiencyScore + constraintScore;

  return {
    accuracy: accuracyScore,
    process: processScore,
    efficiency: efficiencyScore,
    constraint: constraintScore,
    total,
    breakdown: {
      accuracy: Math.round((accuracyScore / 40) * 100),
      process: Math.round((processScore / 30) * 100),
      efficiency: Math.round((efficiencyScore / 20) * 100),
      constraint: Math.round((constraintScore / 10) * 100),
    },
    evidence,
  };
}

// Extract budget number from task description
function extractTaskBudget(task: Task): number | undefined {
  const text = `${task.title} ${task.description}`.toLowerCase();
  const m = text.match(/(?:预算|budget)[:：\s]*[¥￥]?\s*([\d,]+)/);
  return m ? parseInt(m[1].replace(/,/g, '')) : undefined;
}
